import { useEffect, useRef, useState } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalBody, Text, Center, Spinner, Box, Image } from '@chakra-ui/react';
import type { Challenge } from '@src/backend/src/common/types/threeds';
import { decodeHtmlEntities } from '../helpers/threeds';
import mastercardLogo from '../assets/mc-id-check.svg';

interface ChallengeModalProps {
  isOpen: boolean;
  onClose: () => void;
  challenge: Challenge | null;
}

const POLL_INTERVAL_MS = 2000;

export default function ChallengeModal({ isOpen, onClose, challenge }: ChallengeModalProps) {
  if (!challenge) return null;
  return challenge.method === 'wait-for-post' ? (
    <WaitForPostChallenge isOpen={isOpen} onClose={onClose} challenge={challenge} />
  ) : (
    <PollingChallenge isOpen={isOpen} onClose={onClose} challenge={challenge} />
  );
}

function WaitForPostChallenge({
  isOpen,
  onClose,
  challenge,
}: {
  isOpen: boolean;
  onClose: () => void;
  challenge: Extract<Challenge, { method: 'wait-for-post' }>;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasSubmittedRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) {
      hasSubmittedRef.current = false;
      setIsLoading(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMessage = (event: MessageEvent) => {
      if (!event.data?.challengeCompleted) return;
      onCloseRef.current();
    };
    window.addEventListener('message', handleMessage);
    if (challenge.acsUrl && challenge.creqData && !hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      const timer = setTimeout(() => formRef.current?.submit(), 500);
      return () => {
        window.removeEventListener('message', handleMessage);
        clearTimeout(timer);
        hasSubmittedRef.current = false;
      };
    }
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen, challenge.acsUrl, challenge.creqData]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalBody p={0}>
          <Box position="relative" minHeight="500px">
            {isLoading && (
              <Center position="absolute" inset={0} zIndex={10} bg="white">
                <Box textAlign="center">
                  <Spinner size="lg" />
                  <Text fontSize="sm" mt={4} color="gray.500">
                    Cargando formulario de autenticación...
                  </Text>
                </Box>
              </Center>
            )}
            <iframe
              name="challengeIframe"
              id="challengeIframe"
              width="100%"
              height="500"
              style={{ border: 'none', display: 'block', visibility: isLoading ? 'hidden' : 'visible' }}
              onLoad={() => setIsLoading(false)}
            />
            <form
              ref={formRef}
              method="POST"
              action={challenge.acsUrl}
              target="challengeIframe"
              style={{ display: 'none' }}
            >
              <input type="hidden" name="creq" value={challenge.creqData} />
              <input type="hidden" name="threeDSSessionData" value={challenge.sessionData} />
            </form>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function PollingChallenge({
  isOpen,
  onClose,
  challenge,
}: {
  isOpen: boolean;
  onClose: () => void;
  challenge: Extract<Challenge, { method: 'polling' }>;
}) {
  const hiddenContainerRef = useRef<HTMLDivElement>(null);
  const previousStepRef = useRef<string | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [showLogo, setShowLogo] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);
  const [challengeHtml, setChallengeHtml] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    previousStepRef.current = null;
    setShowLogo(false);
    setShowChallenge(false);
    setChallengeHtml(null);
    if (hiddenContainerRef.current) hiddenContainerRef.current.innerHTML = '';

    const interval = setInterval(async () => {
      try {
        const res = await fetch(challenge.resultEndpoint);
        const result = (await res.json()) as {
          status: string;
          payment_method?: {
            extra?: {
              three_ds_auth?: { current_step: string; current_step_status: string; three_ds_method_data?: string };
            };
          };
        };
        const auth = result.payment_method?.extra?.three_ds_auth;

        if (
          result.status !== 'PENDING' ||
          auth?.current_step === 'AUTHENTICATION' ||
          auth?.current_step_status === 'Non-Authenticated' ||
          auth?.current_step_status === 'ERROR'
        ) {
          clearInterval(interval);
          onCloseRef.current();
          return;
        }
        if (!auth) return;
        setShowLogo(true);

        const { current_step: step, current_step_status: stepStatus, three_ds_method_data: iframeHtml } = auth;
        if (step !== previousStepRef.current && stepStatus === 'PENDING' && iframeHtml) {
          const decoded = decodeHtmlEntities(iframeHtml);
          previousStepRef.current = step ?? null;
          if (step === 'SUPPORTED_VERSION') {
            if (hiddenContainerRef.current) hiddenContainerRef.current.innerHTML = decoded;
          } else if (step === 'CHALLENGE') {
            setShowChallenge(true);
            setChallengeHtml(decoded);
          }
        }
      } catch {
        /* skip tick */
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isOpen, challenge]);

  return (
    <>
      {isOpen && <div ref={hiddenContainerRef} style={{ display: 'none' }} />}
      <Modal
        isOpen={showChallenge}
        onClose={() => {}}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        size="lg"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalBody p={0}>
            {showLogo && (
              <Box p={3} borderBottom="1px solid" borderColor="gray.100" display="flex" alignItems="center" gap={2}>
                <Image src={mastercardLogo} alt="Mastercard Identity Check" h="32px" />
              </Box>
            )}
            <Box position="relative" minHeight="400px">
              <Center position="absolute" inset={0} zIndex={10} bg="white" display={challengeHtml ? 'none' : 'flex'}>
                <Box textAlign="center">
                  <Spinner size="lg" />
                  <Text fontSize="sm" mt={4} color="gray.500">
                    Cargando autenticación...
                  </Text>
                </Box>
              </Center>
              {challengeHtml && (
                <iframe
                  srcDoc={challengeHtml}
                  style={{ width: '100%', minHeight: '400px', border: 'none', display: 'block' }}
                  title="3DS Challenge"
                />
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
      {isOpen && showLogo && !showChallenge && (
        <Box
          position="fixed"
          bottom={4}
          right={4}
          zIndex={9999}
          p={2}
          bg="white"
          borderRadius="md"
          shadow="md"
          display="flex"
          alignItems="center"
          gap={2}
        >
          <Image src={mastercardLogo} alt="Mastercard Identity Check" h="32px" />
        </Box>
      )}
    </>
  );
}
